import { useState, useRef, useEffect } from 'react';
import { useHistory } from 'react-router';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Collapse,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  CircularProgress,
  Link,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { resetPassword as apiResetPassword } from '../../Helpers/dataApi';
import PasswordStrengthIndicator from '../../Components/PasswordStrengthIndicator/PasswordStrengthIndicator';
import PageLayout from '../../Components/PageLayout/PageLayout';

export default function ResetPassword() {
  const history = useHistory();
  const redirectTimeout = useRef(null);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({
    password: { error: false, message: '' },
    confirmPassword: { error: false, message: '' },
  });
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    return () => {
      if (redirectTimeout.current) {
        clearTimeout(redirectTimeout.current);
      }
    };
  }, []);

  const validatePasswords = (password, confirmPassword) => {
    const validation = {
      password: { error: false, message: '' },
      confirmPassword: { error: false, message: '' },
    };

    if (!password) {
      validation.password.error = true;
      validation.password.message = 'Please enter a valid password';
      return validation;
    }
    
    if (!confirmPassword) {
      validation.confirmPassword.error = true;
      validation.confirmPassword.message = 'Please enter a valid password';
      return validation;
    }
    
    // Length check
    if (password.length < 8) {
      validation.password.error = true;
      validation.password.message = 'Password must be at least 8 characters';
      return validation;
    }
    
    // Uppercase check
    if (!/[A-Z]/.test(password)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one uppercase letter';
      return validation;
    }
    
    // Lowercase check
    if (!/[a-z]/.test(password)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one lowercase letter';
      return validation;
    }
    
    // Number check
    if (!/\d/.test(password)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one number';
      return validation;
    }
    
    // Special character check
    if (!/[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/;'`~]/.test(password)) {
      validation.password.error = true;
      validation.password.message = 'Password must contain at least one special character';
      return validation;
    }
    
    // Match check
    if (password !== confirmPassword) {
      validation.password.error = true;
      validation.confirmPassword.error = true;
      validation.password.message = 'Passwords do not match';
      validation.confirmPassword.message = 'Passwords do not match';
    }

    return validation;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const validationErrors = validatePasswords(password, confirmPassword);
    setErrors({
      password: validationErrors.password,
      confirmPassword: validationErrors.confirmPassword,
    });
    setFeedback(null);

    if (!validationErrors.password.error && !validationErrors.confirmPassword.error) {
      const token = new URLSearchParams(window.location.search).get('token');
      
      if (!token) {
        setFeedback({
          severity: 'error',
          message: 'Invalid reset link. Please request a new password reset.',
        });
        return;
      }
      
      setLoading(true);
      try {
        const response = await apiResetPassword(token, password);
        setFeedback({
          severity: 'success',
          message: 'Password has been reset successfully. Redirecting to login...',
        });
        redirectTimeout.current = setTimeout(() => history.push('/login'), 3000);
      } catch (error) {
        const errorMsg = error?.response?.data?.error || error?.message || 'An error occurred. Please try again.';
        let userMessage = errorMsg;

        // Provide helpful messages for common errors
        if (errorMsg.includes('expired')) {
          userMessage = 'This reset link has expired. Please request a new password reset.';
        } else if (errorMsg.includes('Invalid') || errorMsg.includes('invalid')) {
          userMessage = 'This reset link is invalid. Please request a new password reset.';
        } else if (errorMsg.includes('uppercase')) {
          userMessage = 'Password must contain at least one uppercase letter.';
        } else if (errorMsg.includes('lowercase')) {
          userMessage = 'Password must contain at least one lowercase letter.';
        } else if (errorMsg.includes('number')) {
          userMessage = 'Password must contain at least one number.';
        } else if (errorMsg.includes('special character')) {
          userMessage = 'Password must contain at least one special character (!@#$%^&*).';
        }

        setFeedback({
          severity: 'error',
          message: userMessage,
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <PageLayout maxWidth="xs">
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flexDirection: 'column',
          marginTop: 8,
        }}
      >
        <Avatar sx={{ m: 1, bgcolor: 'primary.main' }}>
          <LockResetIcon />
        </Avatar>
        <Typography component="h1" variant="h5">
          Change Password
        </Typography>
        <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
          <TextField
            autoComplete="new-password"
            error={errors.password.error}
            fullWidth
            helperText={errors.password.message}
            id="password"
            name="password"
            label="Password"
            type={showPassword ? 'text' : 'password'}
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <PasswordStrengthIndicator password={password} />
          <TextField
            autoComplete="new-password"
            error={errors.confirmPassword.error}
            fullWidth
            helperText={errors.confirmPassword.message}
            id="confirmPassword"
            name="confirmPassword"
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            margin="normal"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle confirm password visibility"
                    onClick={() => setShowConfirmPassword((prev) => !prev)}
                  >
                    {showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            aria-label="back"
            color="primary"
            edge="start"
            onClick={() => history.push('/login')}
            sx={{ borderRadius: 0, fontSize: '1rem', marginLeft: 0 }}
          >
            <ArrowBackIcon sx={{ marginRight: '10px' }} />
            Back
          </IconButton>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirm'}
          </Button>
          {feedback && (
            <Collapse in={!!feedback} sx={{ width: '100%' }}>
              <Alert severity={feedback.severity} sx={{ mt: '0.5rem', textAlign: 'center' }}>
                {feedback.message}
              </Alert>
            </Collapse>
          )}
        </Box>
      </Box>
    </PageLayout>
  );
}
